import type {
  Completion,
  Transaction,
  Goal,
  Chore,
  ChoreProposal,
} from "@/lib/schemas";
import { calculateStreak } from "./streaks";

/**
 * Sianko Odznaki — 49 odznak w 8 kategoriach (zgodne z `odznaki-podglad.html`).
 *
 * Unlocked state computed na bazie:
 *   approved: Completion[] — zatwierdzone zadania dziecka
 *   transactions: Transaction[] — wypłaty + earnings
 *   goals: Goal[] — cele oszczędzania (z `completedAt`)
 *   chores: Chore[] — definicje zadań (do `complete-day` heuristic)
 *   proposals: ChoreProposal[] — propozycje zadań od dziecka
 *   shieldUseCount: liczba zużytych tarcz streaka
 *   userCreatedAt: data utworzenia konta (do `welcome` + `comeback-*`)
 *
 * Wszystkie nowe params w `computeUnlockedAchievements` są opcjonalne —
 * stare wywołania bez tych argumentów dalej działają, tylko nowe
 * achievementy zależne od nich nie odblokują się.
 */
export type AchievementId =
  // ── chores (zielone, 10) ──────────────────────────────────────
  | "first-chore"
  | "five-chores"
  | "ten-chores"
  | "twentyfive-chores"
  | "fifty-chores"
  | "hundred-chores"
  | "twohundred-chores"
  | "daily-five"
  | "weekly-twenty"
  | "complete-day"
  // ── earn (złote, 9) ───────────────────────────────────────────
  | "first-payout"
  | "five-payouts"
  | "ten-payouts"
  | "earned-100"
  | "earned-500"
  | "earned-1000"
  | "earned-2500"
  | "earned-5000"
  | "big-week"
  // ── streak (pomarańczowe, 9) ──────────────────────────────────
  | "streak-3"
  | "streak-7"
  | "streak-14"
  | "streak-30"
  | "streak-50"
  | "streak-100"
  | "streak-365"
  | "shield-first"
  | "shield-veteran"
  // ── goal (fioletowe, 5) ───────────────────────────────────────
  | "first-goal"
  | "three-goals"
  | "five-goals"
  | "big-goal"
  | "huge-goal"
  // ── time (niebieskie, 3) ──────────────────────────────────────
  | "early-bird"
  | "night-owl"
  | "weekend-warrior"
  // ── variety (różowe, 6) ───────────────────────────────────────
  | "variety-pack"
  | "variety-ten"
  | "big-task"
  | "huge-task"
  | "mega-task"
  | "repeat-master"
  // ── family (turkusowe, 3) ─────────────────────────────────────
  | "photo-pro"
  | "family-network"
  | "family-five"
  // ── meta (indigo, 6) ──────────────────────────────────────────
  | "welcome"
  | "comeback-7d"
  | "comeback-30d"
  | "first-proposal"
  | "proposal-five"
  | "collector";

export type AchievementCategory =
  | "chores"
  | "earn"
  | "streak"
  | "goal"
  | "time"
  | "variety"
  | "family"
  | "meta";

export type AchievementTier = 1 | 2 | 3 | 4;

export type Achievement = {
  id: AchievementId;
  title: string;
  description: string;
  emoji: string;
  category: AchievementCategory;
  tier: AchievementTier;
};

export const ACHIEVEMENTS: Achievement[] = [
  // ── chores (10) ───────────────────────────────────────────────
  { id: "first-chore",       title: "Pierwszy krok",        description: "1 zatwierdzone zadanie",                 emoji: "🌱", category: "chores",  tier: 1 },
  { id: "five-chores",       title: "Rozkręcam się",        description: "5 zatwierdzonych zadań",                 emoji: "🚀", category: "chores",  tier: 2 },
  { id: "ten-chores",        title: "Pomocnik",             description: "10 zatwierdzonych zadań",                emoji: "🌟", category: "chores",  tier: 2 },
  { id: "twentyfive-chores", title: "Mocna seria",          description: "25 zatwierdzonych zadań",                emoji: "✨", category: "chores",  tier: 3 },
  { id: "fifty-chores",      title: "Mistrz domu",          description: "50 zatwierdzonych zadań",                emoji: "🏆", category: "chores",  tier: 3 },
  { id: "hundred-chores",    title: "Legenda",              description: "100 zatwierdzonych zadań",               emoji: "👑", category: "chores",  tier: 4 },
  { id: "twohundred-chores", title: "Bohater domu",         description: "200 zatwierdzonych zadań",               emoji: "🦸", category: "chores",  tier: 4 },
  { id: "daily-five",        title: "Pracowita rączka",     description: "5 zadań w jednym dniu",                  emoji: "💨", category: "chores",  tier: 2 },
  { id: "weekly-twenty",     title: "Tydzień Sianka",       description: "20 zadań w tygodniu",                    emoji: "📅", category: "chores",  tier: 3 },
  { id: "complete-day",      title: "Komplet dnia",         description: "Wszystkie codzienne zadania w 1 dzień",  emoji: "✅", category: "chores",  tier: 1 },

  // ── earn (9) ──────────────────────────────────────────────────
  { id: "first-payout",      title: "Pierwsza wypłata",     description: "Odebrana pierwsza tygodniówka",          emoji: "💸", category: "earn",    tier: 1 },
  { id: "five-payouts",      title: "Stały klient",         description: "5 odebranych wypłat",                    emoji: "🏦", category: "earn",    tier: 2 },
  { id: "ten-payouts",       title: "Weteran wypłat",       description: "10 odebranych wypłat",                   emoji: "💵", category: "earn",    tier: 3 },
  { id: "earned-100",        title: "Stówka",               description: "Zarobione 100 zł łącznie",               emoji: "💯", category: "earn",    tier: 2 },
  { id: "earned-500",        title: "Półtysiąca",           description: "Zarobione 500 zł łącznie",               emoji: "💰", category: "earn",    tier: 3 },
  { id: "earned-1000",       title: "Tysiącznik",           description: "Zarobione 1000 zł łącznie",              emoji: "💎", category: "earn",    tier: 3 },
  { id: "earned-2500",       title: "Skarbnica",            description: "Zarobione 2500 zł łącznie",              emoji: "🏛️", category: "earn",   tier: 4 },
  { id: "earned-5000",       title: "Spichlerz pełen",      description: "Zarobione 5000 zł łącznie",              emoji: "🏰", category: "earn",    tier: 4 },
  { id: "big-week",          title: "Worek tygodnia",       description: "100 zł zarobione w jednym tygodniu",     emoji: "💼", category: "earn",    tier: 2 },

  // ── streak (9) ────────────────────────────────────────────────
  { id: "streak-3",          title: "3 dni z rzędu",        description: "Komplet przez 3 dni",                    emoji: "🔥", category: "streak",  tier: 1 },
  { id: "streak-7",          title: "Tydzień ognia",        description: "Komplet przez 7 dni",                    emoji: "🔥", category: "streak",  tier: 2 },
  { id: "streak-14",         title: "Dwa tygodnie",         description: "Komplet przez 14 dni",                   emoji: "⚡", category: "streak",  tier: 3 },
  { id: "streak-30",         title: "Miesiąc bez przerwy",  description: "Komplet przez 30 dni",                   emoji: "🌙", category: "streak",  tier: 3 },
  { id: "streak-50",         title: "Pół setki",            description: "Komplet przez 50 dni",                   emoji: "🌋", category: "streak",  tier: 3 },
  { id: "streak-100",        title: "Setka dni",            description: "Komplet przez 100 dni",                  emoji: "☄️", category: "streak",  tier: 4 },
  { id: "streak-365",        title: "Cały rok Sianka",      description: "Komplet przez 365 dni",                  emoji: "🌌", category: "streak",  tier: 4 },
  { id: "shield-first",      title: "Pierwsza tarcza",      description: "Pierwsze użycie tarczy streaka",         emoji: "🛡️", category: "streak", tier: 1 },
  { id: "shield-veteran",    title: "Pancerz Sianka",       description: "5× użycie tarczy, streak żyje",          emoji: "⚔️", category: "streak",  tier: 3 },

  // ── goal (5) ──────────────────────────────────────────────────
  { id: "first-goal",        title: "Cel zdobyty!",         description: "Pierwszy cel zrealizowany",              emoji: "🎯", category: "goal",    tier: 1 },
  { id: "three-goals",       title: "Łowca celów",          description: "3 cele zrealizowane",                    emoji: "🏅", category: "goal",    tier: 2 },
  { id: "five-goals",        title: "Mistrz celów",         description: "5 celów zrealizowanych",                 emoji: "🎖️", category: "goal",   tier: 3 },
  { id: "big-goal",          title: "Wielki sen",           description: "Cel ≥ 200 zł zrealizowany",              emoji: "🌠", category: "goal",    tier: 3 },
  { id: "huge-goal",         title: "Marzenie spełnione",   description: "Cel ≥ 500 zł zrealizowany",              emoji: "🦄", category: "goal",    tier: 4 },

  // ── time (3) ──────────────────────────────────────────────────
  { id: "early-bird",        title: "Ranny ptaszek",        description: "Zadanie wykonane przed 9 rano",          emoji: "🐦", category: "time",    tier: 1 },
  { id: "night-owl",         title: "Nocna sowa",           description: "Zadanie zatwierdzone po 20:00",          emoji: "🦉", category: "time",    tier: 1 },
  { id: "weekend-warrior",   title: "Weekendowy wojownik",  description: "Zadania w sobotę i niedzielę",           emoji: "🗡️", category: "time",   tier: 2 },

  // ── variety (6) ───────────────────────────────────────────────
  { id: "variety-pack",      title: "Wszechstronny",         description: "5 różnych zadań",                       emoji: "🎨", category: "variety", tier: 1 },
  { id: "variety-ten",       title: "Mistrz wszechstronności", description: "10 różnych zadań",                    emoji: "🎭", category: "variety", tier: 3 },
  { id: "big-task",          title: "Ciężka praca",          description: "Zadanie ≥ 20 zł",                        emoji: "💪", category: "variety", tier: 2 },
  { id: "huge-task",         title: "Wielki projekt",        description: "Zadanie ≥ 50 zł",                        emoji: "🏗️", category: "variety", tier: 3 },
  { id: "mega-task",         title: "Olbrzymie zadanie",     description: "Zadanie ≥ 100 zł",                       emoji: "🚧", category: "variety", tier: 4 },
  { id: "repeat-master",     title: "Mistrz rutyny",         description: "To samo zadanie 30 razy",                emoji: "🔁", category: "variety", tier: 3 },

  // ── family (3) ────────────────────────────────────────────────
  { id: "photo-pro",         title: "Dowód mistrza",        description: "5 zadań ze zdjęciem-dowodem",            emoji: "📸", category: "family",  tier: 2 },
  { id: "family-network",    title: "Cała rodzina pomaga",  description: "Zadania od 3 różnych dorosłych",         emoji: "👨‍👩‍👧", category: "family", tier: 2 },
  { id: "family-five",       title: "Cały klan",            description: "Zadania od 5 różnych dorosłych",         emoji: "🏘️", category: "family", tier: 3 },

  // ── meta (6) ──────────────────────────────────────────────────
  { id: "welcome",           title: "Pierwszy dzień Sianka", description: "Witamy w Sianko!",                      emoji: "👋", category: "meta",    tier: 1 },
  { id: "comeback-7d",       title: "Wieczna młodość",      description: "Powrót po 7+ dniach przerwy",            emoji: "🔄", category: "meta",    tier: 1 },
  { id: "comeback-30d",      title: "Z popiołów",           description: "Powrót po 30+ dniach przerwy",           emoji: "🦅", category: "meta",    tier: 2 },
  { id: "first-proposal",    title: "Sianko-poeta",         description: "Pierwsza propozycja zadania",            emoji: "💡", category: "meta",    tier: 1 },
  { id: "proposal-five",     title: "Pomysłodawca",         description: "5 zaakceptowanych propozycji",           emoji: "✍️", category: "meta",    tier: 2 },
  { id: "collector",         title: "Kolekcjoner Sianka",   description: "Wszystkie pozostałe odznaki zdobyte",    emoji: "💫", category: "meta",    tier: 4 },
];

// ── helpers ─────────────────────────────────────────────────────

const toDate = (t: unknown): Date => {
  if (t instanceof Date) return t;
  if (t && typeof (t as { toDate?: () => Date }).toDate === "function") {
    return (t as { toDate: () => Date }).toDate();
  }
  return new Date(t as string | number);
};

/** Klucz "YYYY-MM-DD" w lokalnej strefie. */
const dayKey = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Klucz tygodnia ISO ("YYYY-Www") — poniedziałek pierwszego dnia. */
const weekKey = (d: Date): string => {
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dow = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - dow);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((+dt - +yearStart) / 86400000 + 1) / 7);
  return `${dt.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
};

/**
 * Oblicza zestaw odblokowanych osiągnięć dla danego dziecka.
 */
export function computeUnlockedAchievements({
  approved,
  transactions,
  goals = [],
  chores = [],
  proposals = [],
  shieldUseCount = 0,
  userCreatedAt,
}: {
  approved: Completion[];
  transactions: Transaction[];
  goals?: Goal[];
  chores?: Chore[];
  proposals?: ChoreProposal[];
  shieldUseCount?: number;
  userCreatedAt?: Date | unknown;
}): Set<AchievementId> {
  const unlocked = new Set<AchievementId>();

  // ── chore counts ──────────────────────────────────────────────
  const choreCount = approved.length;
  if (choreCount >= 1)   unlocked.add("first-chore");
  if (choreCount >= 5)   unlocked.add("five-chores");
  if (choreCount >= 10)  unlocked.add("ten-chores");
  if (choreCount >= 25)  unlocked.add("twentyfive-chores");
  if (choreCount >= 50)  unlocked.add("fifty-chores");
  if (choreCount >= 100) unlocked.add("hundred-chores");
  if (choreCount >= 200) unlocked.add("twohundred-chores");

  // grupowanie wg dnia / tygodnia (do daily-five / weekly-twenty / big-week)
  const perDay = new Map<string, Completion[]>();
  const perWeek = new Map<string, Completion[]>();
  for (const c of approved) {
    const d = toDate(c.completedAt);
    const dk = dayKey(d);
    const wk = weekKey(d);
    (perDay.get(dk) ?? perDay.set(dk, []).get(dk)!).push(c);
    (perWeek.get(wk) ?? perWeek.set(wk, []).get(wk)!).push(c);
  }
  for (const list of perDay.values()) {
    if (list.length >= 5) {
      unlocked.add("daily-five");
      break;
    }
  }
  for (const list of perWeek.values()) {
    if (list.length >= 20) unlocked.add("weekly-twenty");
    const sum = list.reduce((s, c) => s + c.rewardMinor, 0);
    if (sum >= 10000) unlocked.add("big-week"); // 100,00 zł
  }

  // complete-day: dla dowolnego dnia wszystkie codzienne zadania (daily,
  // przypisane do tego usera) mają approved completion z tego dnia.
  if (chores.length > 0 && approved.length > 0) {
    const me = approved[0].completedBy;
    const myDaily = chores.filter(
      (c) =>
        c.active && c.assignedTo === me && c.schedule.type === "daily",
    );
    if (myDaily.length > 0) {
      const myDailyIds = new Set(myDaily.map((c) => c.id));
      for (const dayList of perDay.values()) {
        const doneIds = new Set(dayList.map((c) => c.choreId));
        let allDone = true;
        for (const id of myDailyIds) {
          if (!doneIds.has(id)) {
            allDone = false;
            break;
          }
        }
        if (allDone) {
          unlocked.add("complete-day");
          break;
        }
      }
    }
  }

  // ── payouts ───────────────────────────────────────────────────
  const payouts = transactions.filter((t) => t.type === "payout");
  if (payouts.length >= 1)  unlocked.add("first-payout");
  if (payouts.length >= 5)  unlocked.add("five-payouts");
  if (payouts.length >= 10) unlocked.add("ten-payouts");

  // ── earnings ──────────────────────────────────────────────────
  const totalEarned = approved.reduce((s, c) => s + c.rewardMinor, 0);
  if (totalEarned >= 10000)  unlocked.add("earned-100");
  if (totalEarned >= 50000)  unlocked.add("earned-500");
  if (totalEarned >= 100000) unlocked.add("earned-1000");
  if (totalEarned >= 250000) unlocked.add("earned-2500");
  if (totalEarned >= 500000) unlocked.add("earned-5000");

  // ── streaks ───────────────────────────────────────────────────
  const streak = calculateStreak(approved);
  if (streak >= 3)   unlocked.add("streak-3");
  if (streak >= 7)   unlocked.add("streak-7");
  if (streak >= 14)  unlocked.add("streak-14");
  if (streak >= 30)  unlocked.add("streak-30");
  if (streak >= 50)  unlocked.add("streak-50");
  if (streak >= 100) unlocked.add("streak-100");
  if (streak >= 365) unlocked.add("streak-365");

  // ── shields ───────────────────────────────────────────────────
  if (shieldUseCount >= 1) unlocked.add("shield-first");
  if (shieldUseCount >= 5) unlocked.add("shield-veteran");

  // ── goals ─────────────────────────────────────────────────────
  // Achievementy "big/huge goal" wartościują tylko cele pieniężne
  // (individual_money) — bo "200 zł" nie ma sensu dla family_count/offline.
  // Achievementy ilościowe (first/three/five) liczą wszystkie zaliczone cele.
  const reachedGoals = goals.filter((g) => !!g.completedAt);
  if (reachedGoals.length >= 1) unlocked.add("first-goal");
  if (reachedGoals.length >= 3) unlocked.add("three-goals");
  if (reachedGoals.length >= 5) unlocked.add("five-goals");
  for (const g of reachedGoals) {
    if (g.kind !== "individual_money") continue;
    if (g.targetMinor >= 20000) unlocked.add("big-goal");  // 200 zł
    if (g.targetMinor >= 50000) unlocked.add("huge-goal"); // 500 zł
  }

  // ── time-of-day / weekend ─────────────────────────────────────
  let earlyBird = false;
  let nightOwl = false;
  const weekendDays = new Map<string, Set<number>>();
  for (const c of approved) {
    const d = toDate(c.completedAt);
    const hour = d.getHours();
    if (hour < 9) earlyBird = true;
    if (hour >= 20) nightOwl = true;

    const dow = d.getDay();
    if (dow === 0 || dow === 6) {
      const sat = new Date(d);
      sat.setHours(0, 0, 0, 0);
      sat.setDate(sat.getDate() + (dow === 6 ? 0 : -1));
      const key = dayKey(sat);
      if (!weekendDays.has(key)) weekendDays.set(key, new Set());
      weekendDays.get(key)!.add(dow);
    }
  }
  if (earlyBird) unlocked.add("early-bird");
  if (nightOwl) unlocked.add("night-owl");
  for (const set of weekendDays.values()) {
    if (set.has(6) && set.has(0)) {
      unlocked.add("weekend-warrior");
      break;
    }
  }

  // ── variety & big tasks ───────────────────────────────────────
  const distinctChores = new Set(approved.map((c) => c.choreId));
  if (distinctChores.size >= 5)  unlocked.add("variety-pack");
  if (distinctChores.size >= 10) unlocked.add("variety-ten");

  const maxReward = approved.reduce((m, c) => Math.max(m, c.rewardMinor), 0);
  if (maxReward >= 2000)  unlocked.add("big-task");  // 20 zł
  if (maxReward >= 5000)  unlocked.add("huge-task"); // 50 zł
  if (maxReward >= 10000) unlocked.add("mega-task"); // 100 zł

  // repeat-master: 30× to samo zadanie
  const perChoreCount = new Map<string, number>();
  let maxRepeat = 0;
  for (const c of approved) {
    const n = (perChoreCount.get(c.choreId) ?? 0) + 1;
    perChoreCount.set(c.choreId, n);
    if (n > maxRepeat) maxRepeat = n;
  }
  if (maxRepeat >= 30) unlocked.add("repeat-master");

  // ── proof of work & family ────────────────────────────────────
  const withPhoto = approved.filter((c) => !!c.proofPhotoUrl).length;
  if (withPhoto >= 5) unlocked.add("photo-pro");

  const distinctFunders = new Set(
    approved.map((c) => c.fundedBy).filter((x): x is string => !!x),
  );
  if (distinctFunders.size >= 3) unlocked.add("family-network");
  if (distinctFunders.size >= 5) unlocked.add("family-five");

  // ── meta / specjalne ──────────────────────────────────────────
  if (userCreatedAt) unlocked.add("welcome");

  // comeback: posortuj approved po completedAt, znajdź największy gap
  if (approved.length >= 2) {
    const sorted = [...approved].sort(
      (a, b) => +toDate(a.completedAt) - +toDate(b.completedAt),
    );
    let maxGapDays = 0;
    for (let i = 1; i < sorted.length; i++) {
      const gap =
        (+toDate(sorted[i].completedAt) -
          +toDate(sorted[i - 1].completedAt)) /
        86400000;
      if (gap > maxGapDays) maxGapDays = gap;
    }
    if (maxGapDays >= 7)  unlocked.add("comeback-7d");
    if (maxGapDays >= 30) unlocked.add("comeback-30d");
  }

  // proposals
  if (proposals.length >= 1) unlocked.add("first-proposal");
  const acceptedProposals = proposals.filter(
    (p) => p.status === "approved",
  );
  if (acceptedProposals.length >= 5) unlocked.add("proposal-five");

  // collector — wszystkie pozostałe (oprócz collectora samego)
  const totalNonCollector = ACHIEVEMENTS.length - 1;
  const unlockedNonCollector = unlocked.has("collector")
    ? unlocked.size - 1
    : unlocked.size;
  if (unlockedNonCollector >= totalNonCollector) unlocked.add("collector");

  return unlocked;
}

// ── levels (Sianko-themed, 10 progi z dedykowanymi maskotkami) ───
//
// **Progresja XP** — nieliniowa, każdy następny level coraz trudniejszy:
//   lvl 1: 0-99 XP         (start, Ziarenko)
//   lvl 2: 100-219 XP      (Δ 100)
//   lvl 3: 220-349 XP      (Δ 120)
//   lvl 4: 350-499 XP      (Δ 130)
//   lvl 5: 500-669 XP      (Δ 150)
//   lvl 6: 670-869 XP      (Δ 170)
//   lvl 7: 870-999 XP      (Δ 200)
//   lvl 8: 1000-1299 XP    (Δ 130 → ramp-up zaczyna się)
//   lvl 9: 1300-1699 XP    (Δ 300)
//   lvl 10: 1700-2200+ XP  (Δ 400 → cap, endgame Legenda Sianka)
//
// `LEVEL_CAPS[i]` to próg cumulative XP DO PRZEJŚCIA na level i+2.
// Czyli `xp >= LEVEL_CAPS[0]` = jesteś już na lvl 2+.
//
// Każdy level ma dedykowaną maskotkę w /public/mascot-levels/lvl_N.png.

// ── XP per completion ───────────────────────────────────────────
//
// Pochodne size × kind (patrz `src/lib/utils/xp.ts`). Family daje więcej niż
// extra (pomoc rodzinie > zarobek), M daje 2× S (większy wysiłek/czas).
// Brak `kind` na completion (legacy) → "extra". Brak `size` → "M".

import { getXP } from "./xp";

export function xpForCompletion(
  c: Pick<Completion, "kind"> & Partial<Pick<Completion, "size">>,
): number {
  return getXP(c.kind ?? "extra", c.size ?? "M");
}

/** Suma XP po liście zatwierdzonych completion. Jeden punkt prawdy. */
export function totalXp(
  approved: (Pick<Completion, "kind"> & Partial<Pick<Completion, "size">>)[],
): number {
  let xp = 0;
  for (const c of approved) xp += xpForCompletion(c);
  return xp;
}

export type LevelTitle = {
  level: number;
  displayLevel: number;
  title: string;
  emoji: string;
  mascotPath: string;
};

const LEVEL_DEFS: { title: string; emoji: string }[] = [
  { title: "Ziarenko",           emoji: "🌱" }, // lvl 1
  { title: "Kiełek",             emoji: "🌿" }, // lvl 2
  { title: "Snopek",             emoji: "🌾" }, // lvl 3
  { title: "Kolekcjoner Sianka", emoji: "📦" }, // lvl 4
  { title: "Łowca Sianka",       emoji: "🪃" }, // lvl 5
  { title: "Budowniczy Stogu",   emoji: "🏗️" }, // lvl 6
  { title: "Strażnik Stogu",     emoji: "🛡️" }, // lvl 7
  { title: "Mistrz Pola",        emoji: "🌾" }, // lvl 8
  { title: "Pan Sianka",         emoji: "👑" }, // lvl 9
  { title: "Legenda Sianka",     emoji: "✨" }, // lvl 10+
];

/**
 * Cumulative XP cap dla każdego levela. `LEVEL_CAPS[i]` = XP wymagane
 * żeby AWANSOWAĆ z lvl (i+1). Po przekroczeniu progu jesteś na lvl (i+2).
 *
 *   xp < 100   → lvl 1
 *   100 ≤ xp < 220 → lvl 2
 *   220 ≤ xp < 350 → lvl 3
 *   ...
 *   xp ≥ 2000 → lvl 10 (cap)
 */
export const LEVEL_CAPS = [
  100,  // lvl 1 → lvl 2
  220,  // lvl 2 → lvl 3
  350,  // lvl 3 → lvl 4
  500,  // lvl 4 → lvl 5
  670,  // lvl 5 → lvl 6
  870,  // lvl 6 → lvl 7
  1000, // lvl 7 → lvl 8
  1300, // lvl 8 → lvl 9
  1700, // lvl 9 → lvl 10
  2200, // lvl 10 cap (terminal)
] as const;

/** Zwraca aktualny level (1-10) na podstawie cumulative XP. */
export function levelFromXp(xp: number): number {
  for (let i = 0; i < LEVEL_CAPS.length - 1; i++) {
    if (xp < LEVEL_CAPS[i]) return i + 1;
  }
  return 10;
}

/**
 * Progres w obrębie aktualnego levela.
 * Przykład: xp=150 → level=2, current=50, total=120 (220-100).
 */
export function xpProgressInLevel(xp: number): {
  level: number;
  current: number;
  total: number;
} {
  const level = levelFromXp(xp);
  if (level >= 10) {
    // Na lvl 10 nie ma "next" — pokazujemy progres do cap.
    const prevCap = LEVEL_CAPS[8]; // 1500
    const cap = LEVEL_CAPS[9]; // 2000
    return { level, current: Math.min(xp - prevCap, cap - prevCap), total: cap - prevCap };
  }
  const prevCap = level >= 2 ? LEVEL_CAPS[level - 2] : 0;
  const cap = LEVEL_CAPS[level - 1];
  return { level, current: xp - prevCap, total: cap - prevCap };
}

export function levelTitle(level: number): LevelTitle {
  const clamped = Math.min(Math.max(1, level), 10);
  const def = LEVEL_DEFS[clamped - 1];
  return {
    level,
    displayLevel: clamped,
    title: def.title,
    emoji: def.emoji,
    mascotPath: `/mascot-levels/lvl_${clamped}.png?v=1`,
  };
}

// ── color tokens per kategoria (do BadgeCard / Pillsy) ──────────

export const CATEGORY_LABELS: Record<AchievementCategory, string> = {
  chores: "Zadania",
  earn: "Zarobki",
  streak: "Streaki",
  goal: "Cele",
  time: "Pora dnia",
  variety: "Różnorodność",
  family: "Rodzina",
  meta: "Specjalne",
};

/**
 * Tła per kategoria (light bg) + ring color. Stosowane przez BadgeCard
 * gdy odznaka jest unlocked.
 */
export const CATEGORY_TONES: Record<
  AchievementCategory,
  { bg: string; ring: string }
> = {
  chores:  { bg: "bg-[#DCF1E4]", ring: "ring-forest/30" },
  earn:    { bg: "bg-[#FFEAB0]", ring: "ring-[#7A5A00]/30" },
  streak:  { bg: "bg-[#FFE4CC]", ring: "ring-[#C2410C]/30" },
  goal:    { bg: "bg-[#E8E4FB]", ring: "ring-[#5B49C7]/30" },
  time:    { bg: "bg-[#DBEAFE]", ring: "ring-[#1D4ED8]/30" },
  variety: { bg: "bg-[#FCE7F3]", ring: "ring-[#BE185D]/30" },
  family:  { bg: "bg-[#CCFBF1]", ring: "ring-[#0F766E]/30" },
  meta:    { bg: "bg-[#E0E7FF]", ring: "ring-[#4338CA]/30" },
};

/**
 * Zwraca pierwszą zablokowaną odznakę (z policzonym progresem do unlocku) —
 * używana w "Następna odznaka" card na zakładce Cele. Sortuje po tier
 * (najłatwiejsze pierwsze) w obrębie kategorii zadań/streak/earn.
 */
export type NextBadgeProgress = {
  achievement: Achievement;
  current: number;
  target: number;
};

export function nextUnachievedBadge(
  unlocked: Set<AchievementId>,
  ctx: {
    approved: Completion[];
    transactions: Transaction[];
    streakDays: number;
    distinctChoreCount: number;
    distinctFunderCount: number;
    photoProofCount: number;
  },
): NextBadgeProgress | null {
  const choreCount = ctx.approved.length;
  const totalEarned = ctx.approved.reduce((s, c) => s + c.rewardMinor, 0);
  const payoutsCount = ctx.transactions.filter((t) => t.type === "payout").length;

  // Lista (slug, current, target) dla "ladder" odznak. Pierwsza locked w tej
  // liście to "next" candidate. Sort by tier ASC żeby pokazać blizki cel.
  const ladders: Array<{
    id: AchievementId;
    current: number;
    target: number;
  }> = [
    { id: "first-chore", current: choreCount, target: 1 },
    { id: "five-chores", current: choreCount, target: 5 },
    { id: "ten-chores", current: choreCount, target: 10 },
    { id: "streak-3", current: ctx.streakDays, target: 3 },
    { id: "streak-7", current: ctx.streakDays, target: 7 },
    { id: "twentyfive-chores", current: choreCount, target: 25 },
    { id: "earned-100", current: Math.floor(totalEarned / 100), target: 100 },
    { id: "first-payout", current: payoutsCount, target: 1 },
    { id: "variety-pack", current: ctx.distinctChoreCount, target: 5 },
    { id: "photo-pro", current: ctx.photoProofCount, target: 5 },
    { id: "fifty-chores", current: choreCount, target: 50 },
    { id: "streak-14", current: ctx.streakDays, target: 14 },
    { id: "earned-500", current: Math.floor(totalEarned / 100), target: 500 },
    { id: "streak-30", current: ctx.streakDays, target: 30 },
    { id: "hundred-chores", current: choreCount, target: 100 },
    { id: "earned-1000", current: Math.floor(totalEarned / 100), target: 1000 },
  ];

  for (const item of ladders) {
    if (unlocked.has(item.id)) continue;
    const ach = ACHIEVEMENTS.find((a) => a.id === item.id);
    if (!ach) continue;
    return {
      achievement: ach,
      current: Math.min(item.current, item.target),
      target: item.target,
    };
  }
  return null;
}
